import { useState, useEffect, useCallback, useMemo } from "react";
import { toast } from "react-toastify";

import { listWabaTemplates, sendWabaTemplate } from "../../services/wabaTemplates.api";
import toastError from "../../errors/toastError";
import { parseTemplate, buildVariablesPayload, isTemplateComplete } from "./helpers";

// Lógica del modal selector de plantillas HSM (0367 H7): carga APPROVED de la conexión
// del ticket, arma inputs por variable y envía por POST /waba-templates/send/:ticketId.
export function useWabaTemplateModal({ open, ticketId, whatsappId, onSent, onClose }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [headerMediaUrl, setHeaderMediaUrl] = useState("");
  const [headerTextVars, setHeaderTextVars] = useState([]);
  const [bodyVars, setBodyVars] = useState([]);
  const [sending, setSending] = useState(false);

  const parsed = useMemo(
    () => (selected ? parseTemplate(selected.components) : null),
    [selected]
  );

  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await listWabaTemplates({
        whatsappId: whatsappId || undefined,
        status: "APPROVED",
      });
      setTemplates(Array.isArray(data) ? data : []);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, [whatsappId]);

  // Al abrir: recargar y resetear el flujo.
  useEffect(() => {
    if (!open) return;
    fetchTemplates();
    setSearch("");
    setSelected(null);
    setHeaderMediaUrl("");
    setHeaderTextVars([]);
    setBodyVars([]);
  }, [open, fetchTemplates]);

  const selectTemplate = (tpl) => {
    const p = parseTemplate(tpl.components);
    setSelected(tpl);
    setHeaderMediaUrl("");
    setHeaderTextVars(Array(p.header?.varCount || 0).fill(""));
    setBodyVars(Array(p.body?.varCount || 0).fill(""));
  };

  const backToList = () => setSelected(null);

  const setHeaderVar = (i, v) =>
    setHeaderTextVars((prev) => {
      const n = [...prev];
      n[i] = v;
      return n;
    });

  const setBodyVar = (i, v) =>
    setBodyVars((prev) => {
      const n = [...prev];
      n[i] = v;
      return n;
    });

  const complete = parsed
    ? isTemplateComplete({ parsed, headerMediaUrl, headerTextVars, bodyVars })
    : false;

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return templates;
    return templates.filter((t) => String(t.name).toLowerCase().includes(q));
  }, [templates, search]);

  const handleSend = async () => {
    if (!selected || sending) return;
    setSending(true);
    try {
      const variables = buildVariablesPayload({
        parsed,
        headerMediaUrl,
        headerTextVars,
        bodyVars,
      });
      const { data } = await sendWabaTemplate(ticketId, {
        templateId: selected.id,
        variables,
      });
      toast.success("Plantilla enviada.");
      if (onSent) onSent(data);
      if (onClose) onClose();
    } catch (err) {
      toastError(err);
    } finally {
      setSending(false);
    }
  };

  return {
    filtered,
    loading,
    search,
    setSearch,
    selected,
    parsed,
    selectTemplate,
    backToList,
    headerMediaUrl,
    setHeaderMediaUrl,
    headerTextVars,
    setHeaderVar,
    bodyVars,
    setBodyVar,
    complete,
    sending,
    handleSend,
  };
}
